// QuickNotes.tsx — Kelluvat pikamuistiot operaattorille
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { authFetch } from '../api/client';
import { useFormDraft } from '../hooks/useFormDraft';
import { useDraggable } from '../hooks/useDraggable';
import { getAppZoom } from '../utils/appZoom';
import QrScannerOverlay from './QrScannerOverlay';

interface QuickNoteDraft {
  type: NoteType;
  severity: string;
  content: string;
  urgent: boolean;
}

type NoteType = 'DEVIATION' | 'NOTE' | 'SUPPLY' | 'OTHER';
const TYPE_OPTIONS: { type: NoteType; icon: string; label: string }[] = [
  { type: 'DEVIATION', icon: '⚠️', label: 'Poikkeama' },
  { type: 'NOTE', icon: '📋', label: 'Muistiinpano' },
  { type: 'SUPPLY', icon: '📦', label: 'Tarvike' },
  { type: 'OTHER', icon: '➕', label: 'Muu' },
];
const SEVERITIES = [
  { value: 'LOW', label: 'Matala', color: '#64748b' },
  { value: 'MEDIUM', label: 'Keskitaso', color: '#d97706' },
  { value: 'HIGH', label: 'Korkea', color: '#dc2626' },
  { value: 'CRITICAL', label: 'Kriittinen', color: '#7f1d1d' },
];

interface Note {
  id: string; type: string; severity: string | null; content: string;
  urgent: boolean; createdAt: string;
  linkedDeviation?: { id: string; deviationNumber: string } | null;
  linkedAssignment?: { id: string; description: string } | null;
}

export default function QuickNotes() {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState<Note[]>([]);
  const [step, setStep] = useState<'list' | 'type' | 'form'>('list');
  const [selType, setSelType] = useState<NoteType>('NOTE');
  const [severity, setSeverity] = useState('MEDIUM');
  const [content, setContent] = useState('');
  const [urgent, setUrgent] = useState(false);
  const [saving, setSaving] = useState(false);
  const [listening, setListening] = useState(false);
  const [scanning, setScanning] = useState(false);
  const recognitionRef = useRef<any>(null);
  const navigate = useNavigate();
  // 2.7.2026: tunnistehaku QR-skannauksen rinnalle (huono kamera / pilalle mennyt koodi / tietokone)
  const [serialQuery, setSerialQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchMsg, setSearchMsg] = useState<string | null>(null);
  const [searchMatches, setSearchMatches] = useState<{ ref: string; serial: string; context: string | null }[]>([]);

  // Sprint 36 / H4-B3: desktopissa modaali on siirreltävä otsikkopalkista raahaamalla
  // jotta alapuolella oleva sisältö pysyy näkyvissä. Mobiilissa pidetään
  // alapuolinen sheet-layout (enabled=false), position muistuu localStorageen.
  const [isDesktop, setIsDesktop] = useState<boolean>(() =>
    typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches,
  );
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  // 19.8.2026 (desktop-skaalaus): window.innerWidth on zoomaamaton laitepikseli,
  // mutta arvo sijoitetaan zoomatun kuoren koordinaatistoon → ilman jakoa paneeli
  // lentäisi ruudun ulkopuolelle (ja tallentuisi sinne localStorageen).
  const drag = useDraggable({
    enabled: isDesktop,
    defaultPosition: { x: Math.max(16, window.innerWidth / getAppZoom() - 480), y: 80 },
    storageKey: 'quicknotes',
  });

  // Autosave keskeneräinen luonnos — jos käyttäjä kirjoittaa pitkän sanelun
  // tai poikkeaman ja sessio katkeaa, teksti säilyy palatessa.
  const draftStore = useFormDraft<QuickNoteDraft>('quicknote');

  // Tallennus: joka muutoksella kun form on aktiivinen ja sisältöä on jotain.
  // HUOM: draftStore EI dep-listalla — useFormDraft palauttaa uuden objektin
  // joka renderöinnillä, mikä aiheuttaisi infinite re-runin.
  useEffect(() => {
    if (step !== 'form') return;
    if (!content.trim()) return;
    draftStore.saveDraft({ type: selType, severity, content, urgent });
     
  }, [step, selType, severity, content, urgent]);

  // Käyttäjän tyyppivalinnan jälkeen: jos luonnos on samaa tyyppiä, palauta sisältö.
  // Eri tyyppi → tyhjä form (käyttäjä haluaa selvästi uuden muistion).
  function pickTypeAndOpenForm(type: NoteType) {
    setSelType(type);
    if (draftStore.hasDraft && draftStore.draft?.type === type) {
      const d = draftStore.draft;
      setSeverity(d.severity || 'MEDIUM');
      setContent(d.content || '');
      setUrgent(typeof d.urgent === 'boolean' ? d.urgent : false);
    } else {
      setSeverity('MEDIUM');
      setContent('');
      setUrgent(false);
    }
    setStep('form');
  }

  async function loadNotes() {
    try {
      const res = await authFetch('/operator/notes');
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setNotes(Array.isArray(data) ? data : []);
    } catch {}
  }

  useEffect(() => { loadNotes(); }, []); // Lataa heti mountissa badge-laskuria varten
  useEffect(() => { if (open) loadNotes(); }, [open]);

  async function save() {
    if (!content.trim()) return;
    setSaving(true);
    try {
      const res = await authFetch('/operator/notes', {
        method: 'POST',
        body: JSON.stringify({
          type: selType, content: content.trim(),
          severity: selType === 'DEVIATION' ? severity : undefined,
          urgent: selType === 'SUPPLY' ? urgent : (selType === 'DEVIATION' && (severity === 'HIGH' || severity === 'CRITICAL')),
        }),
      });
      if (!res.ok) {
        let msg = `Tallennus epäonnistui (HTTP ${res.status})`;
        try { const e = await res.json(); if (e?.message) msg = `Tallennus epäonnistui: ${e.message}`; } catch {}
        if (res.status === 401) msg = 'Istuntosi on vanhentunut. Kirjaudu uudelleen.';
        throw new Error(msg);
      }
      setContent(''); setSeverity('MEDIUM'); setUrgent(false);
      draftStore.clearDraft();
      setStep('list');
      loadNotes();
    } catch (err: any) { alert(`Virhe: ${err.message}`); }
    finally { setSaving(false); }
  }

  // Sanelu (Web Speech API)
  function toggleSpeech() {
    if (listening) {
      recognitionRef.current?.stop();
      setListening(false);
      return;
    }
    const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SR) { alert('Puheentunnistus ei tuettu tässä selaimessa'); return; }
    const recognition = new SR();
    recognition.lang = 'fi-FI';
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.onresult = (e: any) => {
      let transcript = '';
      for (let i = 0; i < e.results.length; i++) {
        transcript += e.results[i][0].transcript;
      }
      setContent(transcript);
    };
    recognition.onerror = () => setListening(false);
    recognition.onend = () => setListening(false);
    recognition.start();
    recognitionRef.current = recognition;
    setListening(true);
  }

  const todayCount = notes.length;
  const sevColor = (s: string | null) => SEVERITIES.find(x => x.value === s)?.color ?? '#64748b';

  /** Tunnistehaku: koodi (esim. TE562) → sama /kappale-näkymä kuin QR-skannauksella. */
  async function searchBySerial() {
    const q = serialQuery.trim();
    if (q.length < 2) { setSearchMsg('Anna vähintään 2 merkkiä'); return; }
    setSearching(true); setSearchMsg(null); setSearchMatches([]);
    try {
      const res = await authFetch(`/pieces/search?code=${encodeURIComponent(q)}`);
      if (!res.ok) throw new Error();
      const data = await res.json();
      const matches = data.matches ?? [];
      if (matches.length === 0) setSearchMsg(`Ei löytynyt kappaletta tunnisteella "${q}"`);
      else if (matches.length === 1) { setOpen(false); navigate(`/kappale/${matches[0].ref}`); }
      else setSearchMatches(matches); // useita osumia → käyttäjä valitsee
    } catch {
      setSearchMsg('Haku epäonnistui — yritä uudelleen');
    } finally { setSearching(false); }
  }

  // Sisäinen QR-skanneri (kappaletarra) — fullscreen kameranäkymä päällimmäisenä.
  if (scanning) {
    return <QrScannerOverlay onClose={() => setScanning(false)} />;
  }

  if (!open) {
    return (
      <button onClick={() => { setOpen(true); setStep('list'); }}
        style={{
          // 2.6.2026: nostettu yli OperatorShell:n bottom-navigaation (72px + iPhone-notch).
          // zIndex 110 > bottomNav:n 100 jotta nappi näkyy päällä.
          // 20.8.2026: nappi oli violetti hehkuva 52 px:n ympyrä, joka peitti
          // sisältöä ja toi sivulle yhdeksännen aksenttivärin. Nyt se on
          // pienempi, sovelluksen omassa sävyssä ja hillityllä varjolla —
          // löytyy kun sitä etsii, ei kilpaile päivän työn kanssa. Kelluva
          // nappi peittää yhä osan alla olevasta sisällöstä; sen siirtäminen
          // alapalkkiin on kuoritason päätös, ei tämän sivun.
          position: 'fixed', bottom: 'calc(72px + env(safe-area-inset-bottom, 0px) + 14px)', right: 14, zIndex: 110,
          width: 46, height: 46, borderRadius: '50%',
          background: 'linear-gradient(140deg, #2a6a8a, #3c7c9c)',
          border: '1px solid rgba(255,255,255,0.5)', cursor: 'pointer',
          boxShadow: '0 3px 12px rgba(42,106,138,0.32)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 21,
        }}>
        📝
        {todayCount > 0 && (
          <span style={{
            position: 'absolute', top: -2, right: -2,
            width: 20, height: 20, borderRadius: '50%',
            background: '#dc2626', color: '#fff',
            fontSize: 11, fontWeight: 800,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>{todayCount}</span>
        )}
      </button>
    );
  }

  // Sprint 36 / H4-B3: desktopissa modaali on raahattavissa (left/top staten kautta),
  // mobiilissa pidetään nykyinen bottom-sheet (bottom 72, full-width, max 440).
  const modalStyle: React.CSSProperties = isDesktop && drag.position ? {
    position: 'fixed',
    left: drag.position.x, top: drag.position.y,
    width: 440, maxWidth: 'calc(100vw / var(--app-zoom, 1) - 32px)',
    zIndex: 200,
    background: '#fff', borderRadius: 16,
    boxShadow: '0 12px 40px rgba(0,0,0,0.2)',
    maxHeight: 'calc(80vh / var(--app-zoom, 1))', display: 'flex', flexDirection: 'column',
    border: '1px solid #e2e8f0',
  } : {
    // 2.6.2026: paneli avautuu nyt yli OperatorShell:n bottom-navigaation (72 + safe-area)
    position: 'fixed', bottom: 'calc(72px + env(safe-area-inset-bottom, 0px))', right: 0, left: 0, zIndex: 200,
    maxWidth: 440, margin: '0 auto',
    background: '#fff', borderRadius: '20px 20px 0 0',
    boxShadow: '0 -8px 40px rgba(0,0,0,0.15)',
    maxHeight: 'calc(70vh / var(--app-zoom, 1))', display: 'flex', flexDirection: 'column',
    border: '1px solid #e2e8f0',
  };

  return (
    <div style={modalStyle}>
      {/* Header — desktopissa drag-handle, mobiilissa staattinen */}
      <div
        {...(isDesktop ? drag.dragHandleProps : {})}
        style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '14px 20px', borderBottom: '1px solid #f1f5f9',
          ...(isDesktop ? drag.dragHandleProps.style : {}),
        }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: '#0f1923', display: 'flex', alignItems: 'center', gap: 8 }}>
          {isDesktop && <span style={{ color: '#94a3b8', fontSize: 14, cursor: 'inherit' }} title="Raahaa siirtääksesi">⋮⋮</span>}
          📝 Muistiinpanot
        </div>
        <button onClick={() => setOpen(false)}
          style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#94a3b8', padding: '4px' }}>✕</button>
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: '12px 20px' }}>

        {/* ── Pikavalinta ── */}
        {step === 'list' && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16 }}>
              {TYPE_OPTIONS.map(t => (
                <button key={t.type} onClick={() => pickTypeAndOpenForm(t.type)}
                  style={{
                    padding: '14px 10px', borderRadius: 12, border: '1px solid #e2e8f0',
                    background: '#f8fafc', cursor: 'pointer', fontFamily: 'inherit',
                    display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 600,
                    color: '#0f1923',
                  }}>
                  <span style={{ fontSize: 20 }}>{t.icon}</span>
                  {t.label}
                </button>
              ))}
            </div>

            {/* 25.6.2026: sisäinen QR-skanneri — pysyy sovelluksessa (ei hyppää Safariin) */}
            <button onClick={() => setScanning(true)}
              style={{
                width: '100%', padding: '14px 10px', borderRadius: 12, border: '1px solid #c7d2fe',
                background: '#eef2ff', cursor: 'pointer', fontFamily: 'inherit',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                fontSize: 14, fontWeight: 700, color: '#4338ca', marginBottom: 8,
              }}>
              <span style={{ fontSize: 20 }}>📷</span> Skannaa kappale (QR)
            </button>

            {/* 2.7.2026: tunnistehaku — kun QR ei lue (huono kamera / rikkoutunut koodi / tietokone) */}
            <div style={{ display: 'flex', gap: 8, marginBottom: searchMsg || searchMatches.length ? 8 : 16 }}>
              <input
                value={serialQuery}
                onChange={e => { setSerialQuery(e.target.value); setSearchMsg(null); setSearchMatches([]); }}
                onKeyDown={e => { if (e.key === 'Enter') searchBySerial(); }}
                placeholder="🔤 Tai hae tunnisteella, esim. TE562"
                style={{
                  flex: 1, padding: '11px 12px', borderRadius: 12, border: '1px solid #c7d2fe',
                  background: '#eef2ff', fontSize: 14, fontFamily: 'inherit', color: '#0f1923', minWidth: 0,
                }} />
              <button onClick={searchBySerial} disabled={searching}
                style={{
                  padding: '11px 16px', borderRadius: 12, border: 'none', cursor: searching ? 'default' : 'pointer',
                  background: searching ? '#94a3b8' : '#4338ca', color: '#fff', fontSize: 14, fontWeight: 700, fontFamily: 'inherit',
                }}>
                {searching ? '…' : 'Hae'}
              </button>
            </div>
            {searchMsg && <div style={{ fontSize: 13, color: '#b91c1c', marginBottom: 16 }}>{searchMsg}</div>}
            {searchMatches.length > 1 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', marginBottom: 6 }}>Useita osumia — valitse:</div>
                {searchMatches.map(m => (
                  <button key={m.ref} onClick={() => { setOpen(false); navigate(`/kappale/${m.ref}`); }}
                    style={{
                      width: '100%', textAlign: 'left', padding: '10px 12px', marginBottom: 6, borderRadius: 10,
                      border: '1px solid #e2e8f0', background: '#f8fafc', cursor: 'pointer', fontFamily: 'inherit',
                    }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#0f1923' }}>{m.serial}</div>
                    {m.context && <div style={{ fontSize: 12, color: '#64748b' }}>{m.context}</div>}
                  </button>
                ))}
              </div>
            )}

            {/* Päivän muistiot */}
            {notes.length > 0 && (
              <>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 8 }}>
                  Tänään ({notes.length})
                </div>
                {notes.map(n => (
                  <div key={n.id} style={{
                    padding: '10px 12px', marginBottom: 6, borderRadius: 10,
                    background: '#f8fafc', border: '1px solid #eef2f8',
                    borderLeft: `4px solid ${n.type === 'DEVIATION' ? sevColor(n.severity) : n.type === 'SUPPLY' ? '#d97706' : '#94a3b8'}`,
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: '#64748b' }}>
                        {TYPE_OPTIONS.find(t => t.type === n.type)?.icon} {TYPE_OPTIONS.find(t => t.type === n.type)?.label}
                        {n.severity && <span style={{ color: sevColor(n.severity), marginLeft: 6 }}>{n.severity}</span>}
                      </span>
                      <span style={{ fontSize: 10, color: '#94a3b8' }}>
                        {new Date(n.createdAt).toLocaleTimeString('fi-FI', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    <div style={{ fontSize: 13, color: '#0f1923', lineHeight: 1.4 }}>{n.content}</div>
                    {n.linkedDeviation && (
                      <div style={{ fontSize: 11, color: '#dc2626', marginTop: 4, fontWeight: 600 }}>
                        → Poikkeama {n.linkedDeviation.deviationNumber} luotu
                      </div>
                    )}
                    {n.linkedAssignment && (
                      <div style={{ fontSize: 11, color: '#8b5cf6', marginTop: 4, fontWeight: 600 }}>
                        → Tehtävä luotu: {n.linkedAssignment.description}
                      </div>
                    )}
                  </div>
                ))}
              </>
            )}

            {notes.length === 0 && (
              <div style={{ textAlign: 'center', color: '#94a3b8', fontSize: 13, padding: 20 }}>
                Ei muistiinpanoja tänään. Paina ylläolevaa nappia lisätäksesi.
              </div>
            )}
          </>
        )}

        {/* ── Muistio-lomake ── */}
        {step === 'form' && (
          <>
            <button onClick={() => setStep('list')}
              style={{ background: 'none', border: 'none', color: '#3c7c9c', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', marginBottom: 10, fontWeight: 600 }}>
              ← Takaisin
            </button>

            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
              {TYPE_OPTIONS.find(t => t.type === selType)?.icon} {TYPE_OPTIONS.find(t => t.type === selType)?.label}
            </div>

            {/* Vakavuus (vain poikkeamille) */}
            {selType === 'DEVIATION' && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>Vakavuus</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 4 }}>
                  {SEVERITIES.map(s => (
                    <button key={s.value} onClick={() => setSeverity(s.value)} style={{
                      padding: '8px 4px', borderRadius: 8, fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', textAlign: 'center',
                      border: severity === s.value ? `2px solid ${s.color}` : '1px solid #e2e8f0',
                      background: severity === s.value ? `${s.color}12` : '#fff',
                      color: severity === s.value ? s.color : '#64748b',
                    }}>{s.label}</button>
                  ))}
                </div>
                {(severity === 'HIGH' || severity === 'CRITICAL') && (
                  <div style={{ fontSize: 11, color: '#dc2626', marginTop: 4, fontWeight: 600 }}>
                    {severity === 'CRITICAL' ? '→ Luo välittömän poikkeamailmoituksen managerille' : '→ Luo poikkeamailmoituksen'}
                  </div>
                )}
              </div>
            )}

            {/* Kiireellisyys (tarvikkeille) */}
            {selType === 'SUPPLY' && (
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#64748b', cursor: 'pointer' }}>
                  <input type="checkbox" checked={urgent} onChange={e => setUrgent(e.target.checked)} />
                  Tarvitaan heti (luo tehtäväkortin)
                </label>
              </div>
            )}

            {/* Sisältö + sanelu */}
            <div style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: '#64748b' }}>Kuvaus</span>
                <button onClick={toggleSpeech} style={{
                  padding: '4px 10px', borderRadius: 8, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit',
                  border: listening ? '2px solid #dc2626' : '1px solid #e2e8f0',
                  background: listening ? '#fef2f2' : '#f8fafc',
                  color: listening ? '#dc2626' : '#64748b', fontWeight: 600,
                }}>
                  {listening ? '⏹ Lopeta' : '🎤 Sanele'}
                </button>
              </div>
              <textarea value={content} onChange={e => setContent(e.target.value)}
                placeholder={selType === 'DEVIATION' ? 'Kuvaa havaittu poikkeama...' : selType === 'SUPPLY' ? 'Mikä tuote puuttuu...' : 'Kirjoita muistiinpano...'}
                style={{
                  // boxSizing: ilman tätä width:100% + padding+border ylittävät emoelementin reunan
                  // → Joonan raportoima DEV-2026-3297 ('Kuvaus kirjoituslaatikon reunat menevät ikkunan yli')
                  boxSizing: 'border-box',
                  width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 10,
                  fontSize: 14, fontFamily: 'inherit', minHeight: 80, resize: 'vertical',
                  borderColor: listening ? '#dc2626' : '#e2e8f0',
                }} />
              {listening && <div style={{ fontSize: 11, color: '#dc2626', marginTop: 4 }}>🎤 Kuuntelee... puhu nyt</div>}
            </div>

            <button onClick={save} disabled={!content.trim() || saving}
              style={{
                width: '100%', padding: '14px', borderRadius: 12, border: 'none',
                background: !content.trim() || saving ? '#cbd5e1' : 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                color: '#fff', fontSize: 15, fontWeight: 700, cursor: !content.trim() || saving ? 'default' : 'pointer',
                fontFamily: 'inherit', opacity: !content.trim() || saving ? 0.5 : 1,
              }}>
              {saving ? 'Tallennetaan...' : 'Tallenna'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
